import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, ArrowRight, Building2, GraduationCap } from "lucide-react";
import { COURSES, coursesByCategory, CATEGORIES, LOGO_URL } from "@/lib/data";

const Logo = () => (
    <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
        <img
            src={LOGO_URL}
            alt="7MED"
            className="h-7 sm:h-8 w-auto object-contain"
            draggable="false"
        />
    </Link>
);

const CorsiMegaMenu = ({ onClose }) => {
    const aziende = coursesByCategory("aziende");
    const patente = coursesByCategory("patente");
    return (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[940px] max-w-[95vw]" data-testid="mega-menu-corsi">
            <div className="bg-white border border-ink/10 shadow-2xl">
                <div className="grid grid-cols-1 md:grid-cols-12 divide-x divide-ink/10">
                    {/* Per le aziende */}
                    <div className="md:col-span-7 p-6">
                        <div className="flex items-center gap-3 mb-5 pb-4 border-b-2 border-ink">
                            <div className="w-9 h-9 bg-brand text-white flex items-center justify-center"><Building2 className="w-4 h-4" strokeWidth={2.2} /></div>
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand">{CATEGORIES.aziende.label}</div>
                                <div className="font-heading font-bold text-base">3 corsi</div>
                            </div>
                        </div>
                        <div className="space-y-1">
                            {aziende.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={c.href}
                                    onClick={onClose}
                                    className="flex items-start gap-3 p-3 -mx-3 hover:bg-ink hover:text-white transition-colors group"
                                    data-testid={`mega-menu-${c.slug}`}
                                >
                                    <c.menuIcon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" strokeWidth={1.6} />
                                    <div className="flex-1">
                                        <div className="font-heading font-bold text-sm leading-tight">{c.title}</div>
                                        <div className="text-xs text-ink-muted group-hover:text-white/65 mt-0.5">{c.audienceDescription}</div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                </Link>
                            ))}
                        </div>
                    </div>
                    {/* Per la patente */}
                    <div className="md:col-span-5 p-6 bg-[#FAFAFA]">
                        <div className="flex items-center gap-3 mb-5 pb-4 border-b-2 border-ink">
                            <div className="w-9 h-9 bg-ink text-white flex items-center justify-center"><GraduationCap className="w-4 h-4" strokeWidth={2.2} /></div>
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand">{CATEGORIES.patente.label}</div>
                                <div className="font-heading font-bold text-base">2 corsi</div>
                            </div>
                        </div>
                        <div className="space-y-1">
                            {patente.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={c.href}
                                    onClick={onClose}
                                    className="flex items-start gap-3 p-3 -mx-3 hover:bg-ink hover:text-white transition-colors group"
                                    data-testid={`mega-menu-${c.slug}`}
                                >
                                    <c.menuIcon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" strokeWidth={1.6} />
                                    <div className="flex-1">
                                        <div className="font-heading font-bold text-sm leading-tight">{c.title}</div>
                                        <div className="text-xs text-ink-muted group-hover:text-white/65 mt-0.5">{c.audienceDescription}</div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="bg-ink text-white px-6 py-4 flex items-center justify-between">
                    <div className="text-sm">Non sai quale scegliere?</div>
                    <Link to="/corsi" onClick={onClose} className="text-sm font-bold uppercase tracking-wider text-brand hover:text-white transition-colors inline-flex items-center gap-2">
                        Tutti i corsi <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

const NAV_PRIMARY = [
    { label: "Home", href: "/", testid: "nav-home" },
    { label: "Corsi", href: "/corsi", testid: "nav-corsi", isMega: true },
    { label: "Defibrillatori", href: "/defibrillatori-noleggio", testid: "nav-defibrillatori" },
    { label: "Blog", href: "/blog", testid: "nav-blog" },
    { label: "Chi Siamo", href: "/chi-siamo", testid: "nav-chi-siamo" },
    { label: "Contatti", href: "/contatti", testid: "nav-contatti" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setMegaOpen(false);
    }, [location.pathname]);

    return (
        <>
            <header
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-xl border-b border-ink/10" : "bg-white/70 backdrop-blur-md border-b border-transparent"}`}
                data-testid="main-navbar"
            >
                <div className="container-7m flex items-center justify-between h-16 lg:h-20">
                    <Logo />

                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV_PRIMARY.map((item) =>
                            item.isMega ? (
                                <div key={item.label} className="relative" onMouseEnter={() => setMegaOpen(true)} onMouseLeave={() => setMegaOpen(false)}>
                                    <NavLink to={item.href} className={({ isActive }) => `flex items-center gap-1 px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${isActive || megaOpen ? "text-brand" : "text-ink hover:text-brand"}`} data-testid={item.testid}>
                                        {item.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                                    </NavLink>
                                    {megaOpen && <CorsiMegaMenu onClose={() => setMegaOpen(false)} />}
                                </div>
                            ) : (
                                <NavLink key={item.href} to={item.href} end={item.href === "/"} className={({ isActive }) => `px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${isActive ? "text-brand" : "text-ink hover:text-brand"}`} data-testid={item.testid}>
                                    {item.label}
                                </NavLink>
                            )
                        )}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <a href="tel:+41912615199" className="flex items-center gap-2 text-sm font-mono text-ink-muted hover:text-ink transition-colors" data-testid="navbar-phone">
                            <Phone className="w-4 h-4" /> +41 91 261 51 99
                        </a>
                        <Link to="/richiesta-preventivo" className="btn-primary !py-3 !px-5 !text-xs" data-testid="navbar-cta-quote">Preventivo</Link>
                    </div>

                    <button className="lg:hidden p-2 text-ink" onClick={() => setMobileOpen((s) => !s)} aria-label="Menu" data-testid="navbar-mobile-toggle">
                        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </header>

            {mobileOpen && (
                <div className="fixed inset-0 z-40 lg:hidden pt-16 bg-white overflow-y-auto" data-testid="navbar-mobile-menu">
                    <div className="container-7m py-6 pb-12">
                        <Link to="/" className="block border-b border-ink/10 py-4 font-heading font-bold text-2xl active:text-brand" data-testid="mobile-nav-home">Home</Link>

                        {/* Per le aziende */}
                        <div className="border-b border-ink/10 py-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-brand text-white flex items-center justify-center"><Building2 className="w-4 h-4" strokeWidth={2.2} /></div>
                                <div className="font-heading font-bold text-xl">Per le aziende</div>
                            </div>
                            <div className="space-y-1">
                                {coursesByCategory("aziende").map((c) => (
                                    <Link key={c.slug} to={c.href} className="flex items-center gap-3 py-3 px-3 -mx-3 rounded-sm active:bg-ink/5" data-testid={`mobile-course-${c.slug}`}>
                                        <c.menuIcon className="w-5 h-5 text-brand flex-shrink-0" strokeWidth={1.5} />
                                        <div className="flex-1">
                                            <div className="font-semibold text-base">{c.title}</div>
                                            <div className="text-xs text-ink-muted mt-0.5">{c.audienceDescription}</div>
                                        </div>
                                        <ChevronDown className="w-4 h-4 text-ink-muted -rotate-90 flex-shrink-0" />
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Per la patente */}
                        <div className="border-b border-ink/10 py-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-ink text-white flex items-center justify-center"><GraduationCap className="w-4 h-4" strokeWidth={2.2} /></div>
                                <div className="font-heading font-bold text-xl">Per la patente</div>
                            </div>
                            <div className="space-y-1">
                                {coursesByCategory("patente").map((c) => (
                                    <Link key={c.slug} to={c.href} className="flex items-center gap-3 py-3 px-3 -mx-3 rounded-sm active:bg-ink/5" data-testid={`mobile-course-${c.slug}`}>
                                        <c.menuIcon className="w-5 h-5 text-brand flex-shrink-0" strokeWidth={1.5} />
                                        <div className="flex-1">
                                            <div className="font-semibold text-base">{c.title}</div>
                                            <div className="text-xs text-ink-muted mt-0.5">{c.audienceDescription}</div>
                                        </div>
                                        <ChevronDown className="w-4 h-4 text-ink-muted -rotate-90 flex-shrink-0" />
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <Link to="/defibrillatori-noleggio" className="block border-b border-ink/10 py-4 font-heading font-bold text-2xl active:text-brand" data-testid="mobile-nav-aed">Defibrillatori</Link>
                        <Link to="/blog" className="block border-b border-ink/10 py-4 font-heading font-bold text-2xl active:text-brand" data-testid="mobile-nav-blog">Blog</Link>
                        <Link to="/chi-siamo" className="block border-b border-ink/10 py-4 font-heading font-bold text-2xl active:text-brand" data-testid="mobile-nav-chi-siamo">Chi Siamo</Link>
                        <Link to="/contatti" className="block border-b border-ink/10 py-4 font-heading font-bold text-2xl active:text-brand" data-testid="mobile-nav-contatti">Contatti</Link>

                        <div className="mt-8 space-y-3">
                            <a href="tel:+41912615199" className="btn-secondary w-full py-5 text-sm"><Phone className="w-5 h-5" /> +41 91 261 51 99</a>
                            <Link to="/richiesta-preventivo" className="btn-primary w-full py-5 text-sm">Richiedi Preventivo</Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
